import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getKeycloak } from "../auth/keycloak";
import { fetchPreviousLoginAt, reportAuthAuditEvent } from "../services/authService";

/** @deprecated legacy sessionStorage key; cleared on logout */
export const LOGIN_TIME_KEY = "keycloakLoginTime";

/** Show staff/patient switcher when user has PATIENT plus a staff-type realm/client role. */
export const ACTIVE_PERSONA_KEY = "labtrailsActivePersona";

/** With PATIENT, any of these roles enables switching between staff and patient portal. */
const STAFF_ROLES_FOR_PERSONA_SWITCH = ["ADMIN", "MANAGER", "STAFF"];

const defaultUserState = {
  isAuthenticated: false,
  username: "",
  firstName: "",
  lastName: "",
  fullName: "",
  roles: [],
  loginTime: null,
  activePersona: "staff",
  canSwitchPersona: false,
};

const UserContext = createContext({
  ...defaultUserState,
  refreshUser: async () => {},
  setActivePersona: () => {},
});

function rolesFromParsed(parsed) {
  if (!parsed || typeof parsed !== "object") {
    return [];
  }
  const realmRoles = parsed.realm_access?.roles || [];
  const resourceAccess = parsed.resource_access || {};
  const resourceRoles = Object.values(resourceAccess).flatMap((entry) => entry?.roles || []);
  return [...realmRoles, ...resourceRoles];
}

/**
 * Merge realm + client roles from access and ID tokens. Keycloak often omits some realm roles
 * from the ID token; the access token usually has the full set used by APIs.
 */
function normalizeRoles(kc) {
  const fromAccess = rolesFromParsed(kc?.tokenParsed);
  const fromId = rolesFromParsed(kc?.idTokenParsed);
  const merged = [...fromAccess, ...fromId];
  return Array.from(
    new Set(merged.map((r) => String(r || "").trim()).filter(Boolean))
  );
}

function computeCanSwitchPersona(roles) {
  const upper = (roles || []).map((r) => String(r || "").toUpperCase());
  const hasPatient = upper.includes("PATIENT");
  const hasStaffPortalRole = STAFF_ROLES_FOR_PERSONA_SWITCH.some((r) => upper.includes(r));
  return Boolean(hasPatient && hasStaffPortalRole);
}

function readStoredPersona(roles) {
  if (!computeCanSwitchPersona(roles)) {
    return "staff";
  }
  const raw = sessionStorage.getItem(ACTIVE_PERSONA_KEY);
  if (raw === "patient" || raw === "staff") {
    return raw;
  }
  return "staff";
}

function readUserFromKeycloak() {
  const kc = getKeycloak();

  if (!kc || !kc.authenticated) {
    return { ...defaultUserState };
  }

  const parsed = kc.idTokenParsed || kc.tokenParsed || {};
  const username =
    parsed.preferred_username || parsed.name || parsed.email || parsed.sub || "";

  const firstName = String(parsed.given_name || "").trim();
  const lastName = String(parsed.family_name || "").trim();
  const fullName = `${firstName} ${lastName}`.trim();

  const roles = normalizeRoles(kc);

  return {
    isAuthenticated: true,
    username,
    firstName,
    lastName,
    fullName,
    roles,
    loginTime: null,
    activePersona: readStoredPersona(roles),
    canSwitchPersona: computeCanSwitchPersona(roles),
  };
}

export function UserProvider({ children }) {
  const [user, setUser] = useState(() => readUserFromKeycloak());

  const refreshUser = useCallback(async () => {
    const next = readUserFromKeycloak();
    setUser(next);
    if (!next.isAuthenticated) {
      sessionStorage.removeItem(ACTIVE_PERSONA_KEY);
      return;
    }
    if (!next.canSwitchPersona) {
      sessionStorage.removeItem(ACTIVE_PERSONA_KEY);
    }
    try {
      const iso = await fetchPreviousLoginAt();
      setUser((u) => (u.isAuthenticated ? { ...u, loginTime: iso } : u));
    } catch {
      /* ignore — navbar shows N/A */
    }
  }, []);

  useEffect(() => {
    const kc = getKeycloak();
    if (!kc) {
      return undefined;
    }

    void refreshUser();

    const prevAuthSuccess = kc.onAuthSuccess;
    const prevAuthRefreshSuccess = kc.onAuthRefreshSuccess;
    const prevAuthLogout = kc.onAuthLogout;

    kc.onAuthSuccess = (...args) => {
      if (typeof prevAuthSuccess === "function") {
        prevAuthSuccess(...args);
      }
      void reportAuthAuditEvent("login");
      void refreshUser();
    };
    kc.onAuthRefreshSuccess = (...args) => {
      if (typeof prevAuthRefreshSuccess === "function") {
        prevAuthRefreshSuccess(...args);
      }
      setUser((prev) => {
        const n = readUserFromKeycloak();
        if (!n.isAuthenticated) {
          return { ...defaultUserState };
        }
        return { ...n, loginTime: prev.loginTime };
      });
    };
    kc.onAuthLogout = (...args) => {
      if (typeof prevAuthLogout === "function") {
        prevAuthLogout(...args);
      }
      void reportAuthAuditEvent("logout");
      setUser({ ...defaultUserState });
      sessionStorage.removeItem(ACTIVE_PERSONA_KEY);
    };

    return () => {
      kc.onAuthSuccess = prevAuthSuccess;
      kc.onAuthRefreshSuccess = prevAuthRefreshSuccess;
      kc.onAuthLogout = prevAuthLogout;
    };
  }, [refreshUser]);

  const setActivePersona = useCallback((persona) => {
    const next = persona === "patient" ? "patient" : "staff";
    setUser((prev) => {
      if (!computeCanSwitchPersona(prev.roles)) {
        return prev;
      }
      sessionStorage.setItem(ACTIVE_PERSONA_KEY, next);
      return { ...prev, activePersona: next };
    });
  }, []);

  const value = useMemo(
    () => ({
      ...user,
      refreshUser,
      setActivePersona,
    }),
    [user, refreshUser, setActivePersona]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUserContext() {
  return useContext(UserContext);
}
